<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import ProfilePictureUploadSimple from '@/Components/Forms/ProfilePictureUploadSimple.vue'
import ProfileVideoUpload from '@/Components/Forms/ProfileVideoUpload.vue'

const props = defineProps({
  photo: {
    type: String,
    default: null
  },
  video: {
    type: Object,
    default: () => ({ upload_id: null, playback_id: null })
  },
  slug: {
    type: String,
    required: true
  },
  videoUploadUrlEndpoint: {
    type: String,
    default: null // if not provided, ProfileVideoUpload uses its default (becomeATutor.muxUploadUrl)
  },
  uploadPictureFunction: {
    type: Function,
    required: true
  },
  isUploadingPicture: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:photo', 'update:video'])

// Local photo value
const localPhoto = ref(props.photo)

// Local video values
const localVideoUploadId = ref(props.video?.upload_id || null)
const localVideoPlaybackId = ref(props.video?.playback_id || null)

// Watch for prop changes
watch(() => props.photo, (newVal) => {
  localPhoto.value = newVal
})

watch(() => props.video, (newVal) => {
  localVideoUploadId.value = newVal?.upload_id || null
  localVideoPlaybackId.value = newVal?.playback_id || null
}, { deep: true })

// Emit photo changes
const handlePhotoUpdate = (value) => {
  localPhoto.value = value
  emit('update:photo', value)
}

// Emit video changes
const handleVideoUpdate = (uploadId) => {
  localVideoUploadId.value = uploadId
  // If uploadId is null (reset), also clear the playback_id
  if (uploadId === null) {
    localVideoPlaybackId.value = null
  }
  emit('update:video', {
    upload_id: uploadId,
    playback_id: uploadId === null ? null : localVideoPlaybackId.value
  })
}

const handleVideoComplete = (uploadId) => {
  // Video upload complete, emit the update
  emit('update:video', {
    upload_id: uploadId,
    playback_id: null // Will be populated after Mux processes the video
  })
}
</script>

<template>
  <section class="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-10">
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 mb-2 flex items-center">
        <span class="material-symbols-outlined text-brand-purple mr-3">account_circle</span>
        Profile Media
      </h2>
      <p class="text-gray-600">Upload your profile picture and optionally a video introduction to help students connect with you.</p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Profile Picture Upload -->
      <ProfilePictureUploadSimple
        :model-value="localPhoto"
        :upload-function="uploadPictureFunction"
        :is-uploading="isUploadingPicture"
        :error="errors.photo || errors.avatar_url"
        @update:model-value="handlePhotoUpdate"
      />

      <!-- Profile Video Upload -->
      <ProfileVideoUpload
        :model-value="localVideoUploadId"
        :playback-id="localVideoPlaybackId"
        :slug="slug"
        :upload-url-endpoint="videoUploadUrlEndpoint"
        :error="errors.video"
        @update:model-value="handleVideoUpdate"
        @upload:complete="handleVideoComplete"
      />
    </div>
  </section>
</template>
